import { storage } from '@uhg-abyss/web/tools/storage';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSuppressNiceMembers } from '../../hooks/useSuppressNiceMembers';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { getCurrentMember } from '../../utils/user.utils';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { RadioGroup } from '../RadioGroup';
import { handleLinkAndModalTrack } from '../Utils/adobeTrackUtils';
import { isMnrCosmosMember } from '../Utils/memberUtils/memberUtils';
import { FilterPopOverWrapper } from './Filter.style';
import { Radio, filteredOptions } from './SortByUtil';

type Props = {
  bestMatchTitle?: string;
  smartChoiceTitle?: string;
  content?: string;
  defaultValue?: string | number | boolean;
  title: string;
  label: string | ReactNode;
  options: Radio[];
  model: string;
  onApply: (value: string) => void;
};

export const SortBy = ({
  bestMatchTitle,
  content,
  defaultValue,
  title,
  label,
  options,
  model,
  onApply,
  smartChoiceTitle,
}: Props) => {
  const [showFilter, setShowFilter] = useState(false);
  const { t } = useTranslation();
  const currentMember = getCurrentMember();
  const isCns = currentMember?.isCnSMember;
  const isMnr = isMnrCosmosMember(
    currentMember?.lineOfBusiness,
    currentMember?.population
  );
  const suppressPatientReviews = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );
  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );
  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();

  const filOptions = {
    options,
    suppressPatientReviews,
    suppressPremiumCare:
      suppressPremiumCare || suppressPremiumCareForNiceMembers,
    population: currentMember?.population,
    isMnr,
    isCns,
    isIfp: currentMember?.isIFPMember,
  };

  const handOnApply = (value: string) => {
    setShowFilter(false);
    storage.session.set('sortFilterOpen', false);
    if (value) {
      onApply(value);
    }
  };

  const handleOnOpenChange = () => {
    storage.session.set('sortFilterOpen', !showFilter);
    setShowFilter(!showFilter);
    if (!showFilter) {
      handleLinkAndModalTrack(title, 'body:sort by', title, undefined);
    }
  };
  useEffect(() => {
    storage.session.set('sortFilterOpen', false);
  }, []);

  const renderSortOptions = () => (
    <RadioGroup
      bestMatchTitle={bestMatchTitle}
      content={content}
      defaultValue={defaultValue}
      labelFontSize="16px"
      locationForAnalytics={`modal:${title}`}
      model={model}
      onApply={(value) => handOnApply(value)}
      options={filteredOptions(filOptions)}
      smartChoiceTitle={smartChoiceTitle}
      submitBtnName={t('Sort') as string}
    />
  );

  return (
    <FilterPopOverWrapper>
      <Popover
        aria-label={title}
        content={renderSortOptions()}
        css={{
          'abyss-popover-root': {
            border: 'none',
            borderRadius: '12px',
            padding: '24px',
            width: '308px',
          },
          'abyss-popover-header': {
            marginLeft: '4px',
            marginBottom: '20px',
            padding: '7px 0px',
          },
          'abyss-popover-close-button': {
            top: '29px',
            right: '24px',
            position: 'absolute',
          },
          'abyss-popover-title': {
            marginTop: '0',
            fontSize: '$largeText !important',
            fontWeight: 'bold !important',
          },
        }}
        data-auto-testid={title.replace(/ /g, '-').toLocaleLowerCase()}
        data-testid={title.replace(/ /g, '-').toLocaleLowerCase()}
        onOpenChange={handleOnOpenChange}
        open={showFilter}
        position="bottom"
        title={title}
        width={375}
      >
        {label}
      </Popover>
    </FilterPopOverWrapper>
  );
};
